import { useState, useEffect, useRef } from 'react'
import { X, Clock, User, Wrench, FileText, Camera, MessageSquare, CheckCircle, XCircle, Truck, Package, Download, Send, Play, Pause, AlertTriangle, ChevronDown, MapPin, Phone, Mail, Laptop, ImageIcon, Pencil, Save, Trash2, Paperclip, Receipt, Upload, CreditCard, Loader2 } from 'lucide-react'
import api from '@/lib/api'
import QuoteModal from './QuoteModal'

interface RepairDetailModalProps {
  repairId: number
  onClose: () => void
  onUpdated: () => void
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  requested: { label: 'Solicitada', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  approved: { label: 'Aprobada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  pickup_scheduled: { label: 'Recogida programada', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' },
  in_transit: { label: 'En tránsito', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  received: { label: 'Recibido en taller', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  diagnosing: { label: 'En diagnóstico', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  quote_sent: { label: 'Presupuesto enviado', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  quote_approved: { label: 'Presupuesto aceptado', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  quote_rejected: { label: 'Presupuesto rechazado', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
  waiting_parts: { label: 'Esperando piezas', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  repairing: { label: 'Reparando', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  quality_check: { label: 'Control de calidad', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  not_repairable: { label: 'No reparable', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  payment_pending: { label: 'Pago pendiente', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  payment_confirmed: { label: 'Pago confirmado', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  return_shipped: { label: 'Enviado al cliente', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  delivered: { label: 'Entregado', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  completed: { label: 'Completada', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  cancelled: { label: 'Cancelada', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300' },
}

const REPAIR_TYPE_LABELS: Record<string, string> = {
  screen: 'Pantalla', keyboard: 'Teclado', motherboard: 'Placa base',
  battery: 'Batería', power: 'Carga/Jack', hinge: 'Bisagras',
  casing: 'Carcasa', general: 'Otro', console: 'Consola',
}

export default function RepairDetailModal({ repairId, onClose, onUpdated }: RepairDetailModalProps) {
  const [repair, setRepair] = useState<any>(null)
  const [repairsConfig, setRepairsConfig] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [activeSection, setActiveSection] = useState<string>('info')
  const [actionLoading, setActionLoading] = useState(false)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [showNotRepairableModal, setShowNotRepairableModal] = useState(false)
  const [notRepairableReason, setNotRepairableReason] = useState('')
  const [notRepairableGenerateLabel, setNotRepairableGenerateLabel] = useState(true)

  // Action form states
  const [rejectReason, setRejectReason] = useState('')
  const [diagnosisNotes, setDiagnosisNotes] = useState('')
  const [repairNotes, setRepairNotes] = useState('')
  const [actualHours, setActualHours] = useState<string>('')
  const [qcNotes, setQcNotes] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [comment, setComment] = useState('')
  const [commentPublic, setCommentPublic] = useState(false)
  const [approveResult, setApproveResult] = useState<any>(null)
  const [pickupDate, setPickupDate] = useState('')
  const [pickupTimeSlot, setPickupTimeSlot] = useState('')
  const [pickupNotes, setPickupNotes] = useState('')
  const [waitingPartsNotes, setWaitingPartsNotes] = useState('')
  const [closePayment, setClosePayment] = useState({ method: 'transferencia', invoice_number: '', invoice_amount: '' })
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentInvoice, setPaymentInvoice] = useState<File | null>(null)
  const [editingDevice, setEditingDevice] = useState(false)
  const [deviceModel, setDeviceModel] = useState('')
  const [deviceSerial, setDeviceSerial] = useState('')
  const [editingNotice, setEditingNotice] = useState(false)
  const [orderInput, setOrderInput] = useState('')
  const [linkingOrder, setLinkingOrder] = useState(false)
  const [noticeText, setNoticeText] = useState('')
  const [savingNotice, setSavingNotice] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
  const [editCommentText, setEditCommentText] = useState('')
  const [editCommentPublic, setEditCommentPublic] = useState(false)
  const [commentFiles, setCommentFiles] = useState<File[]>([])
  const [commentEmailStatus, setCommentEmailStatus] = useState<{ ok: boolean; msg: string } | null>(null)
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [resendQuoteEmail, setResendQuoteEmail] = useState('')
  const [resendingQuote, setResendingQuote] = useState(false)
  const [resendingToClient, setResendingToClient] = useState(false)
  const [invoiceMethod, setInvoiceMethod] = useState('transfer')
  const [factusolSearchQuery, setFactusolSearchQuery] = useState('')
  const [factusolSearchResults, setFactusolSearchResults] = useState<any[]>([])
  const [factusolSearching, setFactusolSearching] = useState(false)
  const [showFactusolSearch, setShowFactusolSearch] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraTarget, setCameraTarget] = useState<'new' | number>('new')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const openCamera = (target: 'new' | number) => {
    setCameraTarget(target)
    setShowCamera(true)
  }

  useEffect(() => {
    if (!showCamera) return
    let cancelled = false
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
        }
      })
      .catch(err => {
        console.error('Camera error:', err)
        alert('No se pudo acceder a la cámara')
        setShowCamera(false)
      })
    return () => { cancelled = true; stopCamera() }
  }, [showCamera])

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }

  const capturePhoto = () => {
    const video = videoRef.current
    if (!video) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')!.drawImage(video, 0, 0)
    canvas.toBlob(blob => {
      if (!blob) return
      const file = new File([blob], `camara_${Date.now()}.jpg`, { type: 'image/jpeg' })
      if (cameraTarget === 'new') {
        setCommentFiles(prev => [...prev, file])
      } else {
        const dt = new DataTransfer()
        dt.items.add(file)
        uploadAttachmentsToExisting(cameraTarget, dt.files)
      }
      stopCamera()
      setShowCamera(false)
    }, 'image/jpeg', 0.92)
  }

  useEffect(() => { loadRepair() }, [repairId])

  useEffect(() => {
    api.get('/repairs/admin/config')
      .then(res => setRepairsConfig(res.data))
      .catch(err => console.error('Error loading repairs config:', err))
  }, [])

  const loadRepair = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/repairs/admin/${repairId}`)
      setRepair(res.data)
      if (res.data.diagnosis_notes) setDiagnosisNotes(res.data.diagnosis_notes)
      setDeviceModel(res.data.device_model || '')
      setDeviceSerial(res.data.device_serial || '')
      setNoticeText(res.data.important_notice || '')
    } catch (err) {
      console.error('Error loading repair:', err)
    } finally {
      setLoading(false)
    }
  }

  const doAction = async (endpoint: string, data: any = {}) => {
    setActionLoading(true)
    try {
      const res = await api.post(`/repairs/admin/${repairId}/${endpoint}`, data)
      if (endpoint === 'approve' && res.data) {
        setApproveResult(res.data)
      }
      if (endpoint === 'cancel' && res.data?.pickup_cancellation) {
        const pc = res.data.pickup_cancellation
        if (pc.cancelled) {
          alert(`Reparación cancelada. Recogida ${pc.carrier?.toUpperCase()} ${pc.tracking} también cancelada.`)
        } else {
          alert(
            `Reparación cancelada.\n\n` +
            `La recogida ${pc.carrier?.toUpperCase()} ${pc.tracking} NO se ha podido cancelar ` +
            `(${pc.error || 'motivo desconocido'}). Coordina manualmente con el transportista si procede.`
          )
        }
      }
      await loadRepair()
      onUpdated()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error')
    } finally {
      setActionLoading(false)
    }
  }

  const saveDevice = async () => {
    try {
      await api.put(`/repairs/admin/${repairId}/device`, { device_model: deviceModel, device_serial: deviceSerial })
      setEditingDevice(false)
      loadRepair()
      onUpdated()
    } catch (err) {
      console.error('Error saving device info:', err)
    }
  }

  const saveNotice = async () => {
    if (!noticeText.trim()) { alert('Escribe el texto del aviso') ; return }
    setSavingNotice(true)
    try {
      await api.put(`/repairs/admin/${repairId}/important-notice`, { notice: noticeText })
      setEditingNotice(false)
      await loadRepair()
      onUpdated()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error guardando el aviso')
    } finally {
      setSavingNotice(false)
    }
  }

  const deleteNotice = async () => {
    if (!confirm('¿Eliminar el aviso importante de esta reparación?')) return
    setSavingNotice(true)
    try {
      await api.put(`/repairs/admin/${repairId}/important-notice`, { notice: null })
      setEditingNotice(false)
      setNoticeText('')
      await loadRepair()
      onUpdated()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error eliminando el aviso')
    } finally {
      setSavingNotice(false)
    }
  }

  const addComment = async () => {
    if (!comment.trim() && commentFiles.length === 0) return
    setCommentEmailStatus(null)
    try {
      const wasPublic = commentPublic
      const res = await api.post(`/repairs/admin/${repairId}/comments`, { comment: comment || '(adjunto)', is_public: commentPublic })
      const newCommentId = res.data.comment_id
      // Upload attachments if any
      if (commentFiles.length > 0 && newCommentId) {
        const formData = new FormData()
        commentFiles.forEach(f => formData.append('files', f))
        await api.post(`/repairs/admin/${repairId}/comments/${newCommentId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      // Si el comentario es visible para el cliente, enviarle un email (con imágenes adjuntas)
      if (wasPublic && newCommentId) {
        try {
          await api.post(`/repairs/admin/${repairId}/comments/${newCommentId}/notify`)
          setCommentEmailStatus({ ok: true, msg: 'Email enviado al cliente' })
        } catch (notifyErr: any) {
          console.error('Error notifying comment:', notifyErr)
          setCommentEmailStatus({ ok: false, msg: notifyErr?.response?.data?.detail || 'No se pudo enviar el email al cliente' })
        }
      }
      setComment('')
      setCommentPublic(false)
      setCommentFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      loadRepair()
    } catch (err) {
      console.error('Error adding comment:', err)
    }
  }

  const updateComment = async (commentId: number) => {
    if (!editCommentText.trim()) return
    try {
      await api.put(`/repairs/admin/${repairId}/comments/${commentId}`, { comment: editCommentText, is_public: editCommentPublic })
      setEditingCommentId(null)
      loadRepair()
    } catch (err) {
      console.error('Error updating comment:', err)
    }
  }

  const deleteComment = async (commentId: number) => {
    if (!confirm('¿Eliminar este comentario?')) return
    try {
      await api.delete(`/repairs/admin/${repairId}/comments/${commentId}`)
      loadRepair()
    } catch (err) {
      console.error('Error deleting comment:', err)
    }
  }

  const uploadAttachmentsToExisting = async (commentId: number, files: FileList) => {
    setUploadingAttachments(true)
    try {
      const formData = new FormData()
      Array.from(files).forEach(f => formData.append('files', f))
      await api.post(`/repairs/admin/${repairId}/comments/${commentId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      loadRepair()
    } catch (err) {
      console.error('Error uploading attachments:', err)
    } finally {
      setUploadingAttachments(false)
    }
  }

  const downloadLabel = async (type: 'pickup' | 'return') => {
    try {
      const res = await api.get(`/repairs/admin/${repairId}/label/${type}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `etiqueta_${type}_${repair.code}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('Etiqueta no disponible')
    }
  }

  if (loading || !repair) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center">Cargando...</div>
      </div>
    )
  }

  const st = STATUS_LABELS[repair.status] || { label: repair.status, color: 'bg-slate-100 text-slate-600' }

  // Presupuesto rechazado de cliente PVP con tarifa de diagnóstico aplicada → se factura el
  // transporte (recogida + devolución), igual que un equipo no reparable. Basado en quote_response
  // (no en el estado) para poder facturar aunque el equipo ya se haya devuelto.
  const rejectedWithFee = repair.quote_response === 'rejected' && !!repair.diagnostic_fee_applied && (repair.diagnostic_fee || 0) > 0
  // Facturas de "gastos de transporte" (no reparable o presupuesto rechazado) vs factura completa del presupuesto
  const billsTransportFee = repair.status === 'not_repairable' || rejectedWithFee

  // Determine available actions based on status
  const actions: { label: string; action: () => void; color: string; icon: any }[] = []

  if (repair.status === 'requested') {
    actions.push({ label: 'Aprobar', action: () => doAction('approve', { pickup_date: pickupDate || undefined, pickup_time_slot: pickupTimeSlot || undefined, carrier_notes: pickupNotes || undefined }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    actions.push({ label: 'Rechazar', action: () => { if (rejectReason) doAction('reject', { rejection_reason: rejectReason }); else alert('Indica el motivo de rechazo') }, color: 'bg-red-600 hover:bg-red-700', icon: XCircle })
  }
  if (['in_transit', 'pickup_scheduled', 'approved'].includes(repair.status)) {
    actions.push({ label: 'Marcar recibido', action: () => doAction('received', {}), color: 'bg-indigo-600 hover:bg-indigo-700', icon: Package })
  }
  if (['received', 'diagnosing'].includes(repair.status)) {
    actions.push({ label: 'Guardar diagnóstico', action: () => { if (diagnosisNotes) doAction('diagnosis', { diagnosis_notes: diagnosisNotes }); else alert('Escribe el diagnóstico') }, color: 'bg-violet-600 hover:bg-violet-700', icon: FileText })
  }
  if (repair.status === 'diagnosing') {
    actions.push({ label: 'Crear presupuesto', action: () => setShowQuoteModal(true), color: 'bg-orange-600 hover:bg-orange-700', icon: FileText })
    actions.push({ label: 'Cerrar sin cargo', action: () => { if (diagnosisNotes) doAction('close-no-charge', { reason: diagnosisNotes }); else alert('Escribe el motivo en el diagnóstico') }, color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    actions.push({ label: 'No reparable', action: () => { setNotRepairableReason(diagnosisNotes || ''); setNotRepairableGenerateLabel(true); setShowNotRepairableModal(true) }, color: 'bg-red-600 hover:bg-red-700', icon: XCircle })
  }
  if (repair.status === 'quote_sent') {
    actions.push({ label: 'Presupuesto rechazado', action: () => { const reason = prompt('Motivo del rechazo (opcional):'); if (reason !== null) doAction('reject-quote', { reason: reason || undefined }) }, color: 'bg-red-600 hover:bg-red-700', icon: XCircle })
  }
  if (repair.status === 'quote_approved') {
    actions.push({ label: 'Esperando piezas', action: () => doAction('waiting-parts', { notes: waitingPartsNotes }), color: 'bg-yellow-600 hover:bg-yellow-700', icon: Pause })
    actions.push({ label: 'Iniciar reparación', action: () => doAction('start-repair', {}), color: 'bg-blue-600 hover:bg-blue-700', icon: Play })
    actions.push({ label: 'No reparable', action: () => { setNotRepairableReason(''); setNotRepairableGenerateLabel(true); setShowNotRepairableModal(true) }, color: 'bg-red-600 hover:bg-red-700', icon: XCircle })
  }
  if (repair.status === 'waiting_parts') {
    actions.push({ label: 'Iniciar reparación', action: () => doAction('start-repair', {}), color: 'bg-blue-600 hover:bg-blue-700', icon: Play })
  }
  if (repair.status === 'repairing') {
    actions.push({ label: 'Completar reparación', action: () => doAction('complete-repair', { repair_notes: repairNotes || undefined, actual_hours: actualHours ? parseFloat(actualHours) : undefined }), color: 'bg-purple-600 hover:bg-purple-700', icon: CheckCircle })
  }
  if (repair.status === 'quality_check') {
    actions.push({ label: 'QC Aprobado', action: () => doAction('quality-check', { qc_result: 'passed', qc_notes: qcNotes || undefined }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    actions.push({ label: 'QC Fallido', action: () => doAction('quality-check', { qc_result: 'failed', qc_notes: qcNotes || 'Fallo en control de calidad' }), color: 'bg-red-600 hover:bg-red-700', icon: XCircle })
  }
  // After QC passed or not_repairable: if invoice created + PDF uploaded → show "Solicitar pago"
  if (((repair.status === 'quality_check' && repair.qc_result === 'passed') || repair.status === 'not_repairable' || rejectedWithFee) && repair.invoice_number && repair.payment_invoice_pdf) {
    actions.push({ label: 'Solicitar pago', action: async () => {
      const amt = paymentAmount || String(repair.invoice_amount || repair.quote_total || 0)
      if (!amt || parseFloat(amt) <= 0) { alert('Indica el importe'); return }
      const formData = new FormData()
      formData.append('method', paymentMethod)
      formData.append('amount', amt)
      setActionLoading(true)
      try {
        await api.post(`/repairs/admin/${repairId}/request-payment`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        await loadRepair()
        onUpdated()
      } catch (err: any) { alert(err.response?.data?.detail || 'Error') }
      finally { setActionLoading(false) }
    }, color: 'bg-emerald-600 hover:bg-emerald-700', icon: Send })
  }
  if (repair.status === 'payment_pending') {
    actions.push({ label: 'Confirmar pago', action: () => doAction('confirm-payment', {}), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
  }
  if (repair.status === 'not_repairable') {
    if (repair.skip_return_shipment) {
      // Sin etiqueta: cerrar el caso directamente (no se devuelve el equipo)
      actions.push({ label: 'Cerrar sin devolución', action: () => doAction('close', { payment_status: 'no_charge', invoice_number: repair.invoice_number || undefined, invoice_amount: repair.invoice_amount || undefined }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    } else if (repair.shipping_option !== 'in_store') {
      actions.push({ label: 'Enviar al cliente', action: () => doAction('ship-return', {}), color: 'bg-teal-600 hover:bg-teal-700', icon: Truck })
    }
  }
  if (repair.status === 'quote_rejected' || repair.status === 'payment_confirmed') {
    if (repair.skip_return_shipment) {
      // PVP no reparable cobrado, sin etiqueta: cerrar caso
      actions.push({ label: 'Cerrar sin devolución', action: () => doAction('close', { payment_method: closePayment.method, invoice_number: closePayment.invoice_number || repair.invoice_number || undefined, invoice_amount: closePayment.invoice_amount ? parseFloat(closePayment.invoice_amount) : (repair.invoice_amount || undefined) }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    } else if (repair.return_tracking) {
      // El equipo ya se envió (p.ej. presupuesto rechazado cobrado a posteriori): cerrar sin reenviar
      actions.push({ label: 'Cerrar caso', action: () => doAction('close', { payment_method: closePayment.method, invoice_number: closePayment.invoice_number || repair.invoice_number || undefined, invoice_amount: closePayment.invoice_amount ? parseFloat(closePayment.invoice_amount) : (repair.invoice_amount || undefined) }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    } else if (repair.shipping_option === 'in_store') {
      // En tienda: cerrar caso directamente (el cliente recoge en local)
      actions.push({ label: 'Entregado al cliente', action: () => doAction('close', { payment_method: closePayment.method, invoice_number: closePayment.invoice_number || undefined, invoice_amount: closePayment.invoice_amount ? parseFloat(closePayment.invoice_amount) : undefined }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
    } else {
      actions.push({ label: 'Enviar al cliente', action: () => doAction('ship-return', {}), color: 'bg-teal-600 hover:bg-teal-700', icon: Truck })
    }
  }
  if (repair.status === 'return_shipped') {
    actions.push({ label: 'Marcar entregado', action: () => doAction('mark-delivered', {}), color: 'bg-green-600 hover:bg-green-700', icon: CheckCircle })
  }
  if (repair.status === 'delivered') {
    actions.push({ label: 'Cerrar caso', action: () => doAction('close', { payment_method: closePayment.method, invoice_number: closePayment.invoice_number || undefined, invoice_amount: closePayment.invoice_amount ? parseFloat(closePayment.invoice_amount) : undefined }), color: 'bg-emerald-600 hover:bg-emerald-700', icon: CheckCircle })
  }

  const canCancel = [
    'requested', 'approved', 'pickup_scheduled',
    'in_transit', 'received', 'diagnosing',
    'quote_sent', 'quote_rejected',
    'not_repairable', 'payment_pending',
  ].includes(repair.status)

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8" onClick={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl mx-4" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <Wrench className="w-5 h-5 text-indigo-500" />
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">{repair.code}</h2>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400">{repair.customer_type}</span>
              {repair.shipping_option === 'in_store' && <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">En tienda</span>}
            </div>
            <div className="flex items-center gap-2">
              {!repair.important_notice && !editingNotice && (
                <button
                  onClick={() => { setNoticeText(''); setEditingNotice(true) }}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Añadir un aviso importante que se mostrará siempre al abrir esta reparación"
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Añadir aviso
                </button>
              )}
              <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
          </div>

          {/* Aviso importante — visible siempre, en todas las pestañas */}
          {(repair.important_notice || editingNotice) && (
            <div className="mx-6 mt-4 rounded-lg border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-red-100/70 dark:bg-red-900/30">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <span className="text-xs font-bold text-red-700 dark:text-red-300 uppercase tracking-wide">Aviso importante</span>
                </div>
                {!editingNotice && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setNoticeText(repair.important_notice || ''); setEditingNotice(true) }}
                      className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 transition-colors" title="Editar aviso">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={deleteNotice} disabled={savingNotice}
                      className="p-1 text-red-500 hover:text-red-700 dark:hover:text-red-300 transition-colors disabled:opacity-50" title="Eliminar aviso">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="px-4 py-3">
                {editingNotice ? (
                  <div className="space-y-2">
                    <textarea
                      value={noticeText}
                      onChange={e => setNoticeText(e.target.value)}
                      rows={3}
                      autoFocus
                      placeholder="Ej: NO entregar el equipo sin cobrar la factura pendiente. El cliente pidió que le llamemos antes de enviar..."
                      className="w-full px-3 py-2 text-sm border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    />
                    <div className="flex items-center gap-2">
                      <button onClick={saveNotice} disabled={savingNotice}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50">
                        <Save className="w-3.5 h-3.5" />
                        {savingNotice ? 'Guardando...' : 'Guardar aviso'}
                      </button>
                      <button onClick={() => { setEditingNotice(false); setNoticeText(repair.important_notice || '') }} disabled={savingNotice}
                        className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-red-900 dark:text-red-100 whitespace-pre-wrap">{repair.important_notice}</p>
                    {repair.important_notice_by && (
                      <p className="text-[11px] text-red-500 dark:text-red-400/80 mt-1.5">
                        Por {repair.important_notice_by}{repair.important_notice_at ? ` — ${new Date(repair.important_notice_at).toLocaleString('es-ES')}` : ''}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Section tabs */}
          <div className="flex border-b border-slate-200 dark:border-slate-700 px-6">
            {[
              { id: 'info', label: 'Información', icon: User },
              { id: 'actions', label: 'Acciones', icon: Wrench },
              { id: 'quote', label: 'Presupuesto', icon: FileText },
              { id: 'timeline', label: 'Historial', icon: Clock },
              { id: 'comments', label: 'Comentarios', icon: MessageSquare },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 transition-colors ${activeSection === tab.id ? 'border-indigo-500 text-indigo-600 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                <tab.icon className="w-3.5 h-3.5" />{tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Info tab */}
            {activeSection === 'info' && (() => {
              const token = localStorage.getItem('jvs_auth_token') || ''
              return (
              <div className="space-y-5">
                {/* Top cards: Cliente + Dispositivo */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Cliente */}
                  <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-blue-500" />
                      <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Cliente</h4>
                    </div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{repair.customer_name}</p>
                    <div className="mt-2 space-y-1">
                      <p className="flex items-center gap-1.5 text-xs text-slate-500"><Mail className="w-3 h-3" />{repair.customer_email}</p>
                      {repair.customer_phone && <p className="flex items-center gap-1.5 text-xs text-slate-500"><Phone className="w-3 h-3" />{repair.customer_phone}</p>}
                      {repair.customer_nif && <p className="text-xs text-slate-500">NIF: {repair.customer_nif}</p>}
                      <p className="text-xs text-slate-500">
                        {repair.customer_id
                          ? <>Cliente tienda <a href={`https://www.jvs-informatica.com/admin-dev/index.php?controller=AdminCustomers&id_customer=${repair.customer_id}&viewcustomer`} target="_blank" rel="noreferrer" className="font-mono text-indigo-600 hover:underline">#{repair.customer_id}</a></>
                          : <span className="text-slate-400">Sin cuenta en la tienda</span>}
                        {repair.factusol_client_code ? <> · Factusol <span className="font-mono">#{repair.factusol_client_code}</span></> : null}
                      </p>
                      <div className="pt-1">
                        {repair.order_id ? (
                          <p className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">Pedido tienda <span className="font-mono">{repair.order_reference}</span> (#{repair.order_id})
                            <button onClick={async () => { if (!confirm('¿Desvincular el pedido?')) return; setLinkingOrder(true); try { await api.post(`/repairs/admin/${repairId}/link-order`, { order: null }); await loadRepair() } catch (err: any) { alert(err.response?.data?.detail || 'Error') } finally { setLinkingOrder(false) } }} className="text-rose-500 hover:text-rose-600" disabled={linkingOrder}>quitar</button>
                          </p>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <input value={orderInput} onChange={e => setOrderInput(e.target.value)} placeholder="Pedido tienda (id o referencia)"
                              onKeyDown={async e => { if (e.key === 'Enter' && orderInput.trim()) { setLinkingOrder(true); try { await api.post(`/repairs/admin/${repairId}/link-order`, { order: orderInput.trim() }); setOrderInput(''); await loadRepair() } catch (err: any) { alert(err.response?.data?.detail || 'Error') } finally { setLinkingOrder(false) } } }}
                              className="flex-1 px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                            <button disabled={linkingOrder || !orderInput.trim()}
                              onClick={async () => { setLinkingOrder(true); try { await api.post(`/repairs/admin/${repairId}/link-order`, { order: orderInput.trim() }); setOrderInput(''); await loadRepair() } catch (err: any) { alert(err.response?.data?.detail || 'Error') } finally { setLinkingOrder(false) } }}
                              className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 rounded hover:bg-slate-200 disabled:opacity-40">Vincular</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Dispositivo */}
                  <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Laptop className="w-4 h-4 text-violet-500" />
                        <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Dispositivo</h4>
                      </div>
                      {!editingDevice ? (
                        <button onClick={() => setEditingDevice(true)} className="text-slate-400 hover:text-violet-500 transition-colors" title="Editar dispositivo">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button onClick={saveDevice} className="text-emerald-500 hover:text-emerald-600 transition-colors" title="Guardar">
                            <Save className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => { setEditingDevice(false); setDeviceModel(repair.device_model || ''); setDeviceSerial(repair.device_serial || '') }} className="text-slate-400 hover:text-slate-600 transition-colors" title="Cancelar">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    {!editingDevice ? (
                      <>
                        <p className="font-semibold text-slate-900 dark:text-white text-sm">{repair.device_brand === 'Otro' ? repair.device_brand_other : repair.device_brand} {repair.device_model || ''}</p>
                        <div className="mt-2 space-y-1">
                          {repair.device_serial && <p className="text-xs text-slate-500">S/N: <span className="font-mono">{repair.device_serial}</span></p>}
                          <p className="text-xs"><span className="inline-flex items-center px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-medium">{REPAIR_TYPE_LABELS[repair.repair_type] || repair.repair_type || 'General'}</span></p>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-[10px] text-slate-400 uppercase mb-0.5">Modelo</label>
                          <input value={deviceModel} onChange={e => setDeviceModel(e.target.value)} placeholder="Ej: ThinkPad T480"
                            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                        </div>
                        <div>
                          <label className="block text-[10px] text-slate-400 uppercase mb-0.5">Número de serie</label>
                          <input value={deviceSerial} onChange={e => setDeviceSerial(e.target.value)} placeholder="S/N del dispositivo"
                            className="w-full px-2.5 py-1.5 text-sm font-mono border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                        </div>
                        <p className="text-xs"><span className="inline-flex items-center px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-medium">{REPAIR_TYPE_LABELS[repair.repair_type] || repair.repair_type || 'General'}</span></p>
                      </div>
                    )}
                    {repair.accessories_included && (Array.isArray(repair.accessories_included) ? repair.accessories_included : []).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-600">
                        <p className="text-[10px] text-slate-400 uppercase mb-1">Accesorios</p>
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(repair.accessories_included) ? repair.accessories_included : []).map((a: string, i: number) => (
                            <span key={i} className="px-1.5 py-0.5 text-[10px] bg-slate-200 dark:bg-slate-600 rounded text-slate-600 dark:text-slate-300">{a}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Problema */}
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />Problema reportado</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{repair.problem_description || 'Sin descripción'}</p>
                </div>

                {/* Diagnóstico */}
                {repair.diagnosis_notes && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800/30 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Diagnóstico</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{repair.diagnosis_notes}</p>
                    {repair.diagnosis_by && <p className="text-xs text-slate-500 mt-2">Por {repair.diagnosis_by} — {repair.diagnosed_at ? new Date(repair.diagnosed_at).toLocaleString('es-ES') : ''}</p>}
                  </div>
                )}

                {/* Fotos del cliente */}
                {repair.photos && repair.photos.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" />Fotos del dispositivo ({repair.photos.length})</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {repair.photos.map((p: any) => (
                        <a key={p.id} href={`/api/repairs/photo/${p.id}?token=${token}`} target="_blank" rel="noreferrer"
                          className="block rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 hover:border-blue-400 transition-colors">
                          <img src={`/api/repairs/photo/${p.id}?token=${token}`} alt={p.filename}
                            className="w-full h-24 object-cover" />
                          <div className="px-2 py-1 bg-slate-50 dark:bg-slate-700/50">
                            <p className="text-[10px] text-slate-400 truncate">{p.photo_stage} · {p.uploaded_by}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dirección + Transporte */}
                <div className="grid grid-cols-2 gap-4">
                  {repair.pickup_address && (
                    <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-emerald-500" />
                        <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Dirección recogida</h4>
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-300 space-y-0.5">
                        <p>{repair.pickup_address.street}</p>
                        <p>{repair.pickup_address.postal_code} {repair.pickup_address.city}</p>
                        <p>{repair.pickup_address.province} ({repair.pickup_address.country_code || 'ES'})</p>
                      </div>
                    </div>
                  )}
                  {(repair.pickup_tracking || repair.return_tracking) && (
                    <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-4 h-4 text-cyan-500" />
                        <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Transporte</h4>
                      </div>
                      <div className="space-y-2">
                        {repair.pickup_tracking && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Recogida ({repair.pickup_carrier_code?.toUpperCase()})</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">{repair.pickup_tracking}</span>
                              {repair.has_pickup_label && <button onClick={() => downloadLabel('pickup')} className="text-blue-500 hover:text-blue-700"><Download className="w-3.5 h-3.5" /></button>}
                            </div>
                          </div>
                        )}
                        {repair.return_tracking && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Devolución</span>
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">{repair.return_tracking}</span>
                              {repair.has_return_label && <button onClick={() => downloadLabel('return')} className="text-blue-500 hover:text-blue-700"><Download className="w-3.5 h-3.5" /></button>}
                            </div>
                          </div>
                        )}
                        {repair.pickup_date && (
                          <p className="text-xs text-slate-500">Recogida: {new Date(repair.pickup_date).toLocaleDateString('es-ES')} {repair.pickup_time_slot ? `(${repair.pickup_time_slot})` : ''}</p>
                        )}
                        {repair.pickup_sibling_repairs?.length > 0 && (
                          <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded text-xs">
                            <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Recogida agrupada</p>
                            <p className="text-amber-600 dark:text-amber-300">
                              Comparte recogida con: {repair.pickup_sibling_repairs.map((r: any) => r.code).join(', ')}
                            </p>
                          </div>
                        )}
                        {repair.return_sibling_repairs?.length > 0 && (
                          <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded text-xs">
                            <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Devolución agrupada</p>
                            <p className="text-amber-600 dark:text-amber-300">
                              Comparte envío con: {repair.return_sibling_repairs.map((r: any) => r.code).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* IVA intracomunitario (clientes UE fuera de España) */}
                {repair.pickup_address?.country_code && repair.pickup_address.country_code !== 'ES' && (
                  <div className={`rounded-lg p-4 border ${repair.vat_valid ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/30' : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/30'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h4 className={`text-xs font-semibold uppercase mb-1 ${repair.vat_valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>IVA cliente UE ({repair.pickup_address.country_code})</h4>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          {repair.vat_valid
                            ? <>Sin IVA: NIF <span className="font-mono">{repair.vat_number}</span> válido en VIES{repair.vat_company_name ? ` (${repair.vat_company_name})` : ''}</>
                            : repair.vat_checked_at
                              ? <>Con IVA español: NIF {repair.vat_number ? <span className="font-mono">{repair.vat_number}</span> : 'sin indicar'} no válido en VIES (particular o no registrado)</>
                              : <>Pendiente de validar en VIES (se hace al presupuestar y al facturar). Sin NIF válido se aplica IVA español.</>}
                        </p>
                        {repair.vat_checked_at && <p className="text-[11px] text-slate-500 mt-1">Comprobado: {new Date(repair.vat_checked_at).toLocaleString('es-ES')}</p>}
                      </div>
                      <button
                        onClick={async () => { setActionLoading(true); try { await api.post(`/repairs/admin/${repairId}/check-vat`); await loadRepair() } catch (err: any) { alert(err.response?.data?.detail || 'Error consultando VIES') } finally { setActionLoading(false) } }}
                        disabled={actionLoading}
                        className="px-3 py-1.5 text-xs font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-white dark:hover:bg-slate-700 whitespace-nowrap disabled:opacity-50">
                        Validar en VIES
                      </button>
                    </div>
                  </div>
                )}

                {/* Notas internas */}
                {repair.internal_notes && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase mb-2">Notas internas</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{repair.internal_notes}</p>
                  </div>
                )}
              </div>
            )})()}

            {/* Actions tab */}
            {activeSection === 'actions' && (
              <div className="space-y-4">
                {/* Approve result summary */}
                {approveResult && (
                  <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4 space-y-2">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" /> Reparación aprobada
                    </h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        {approveResult.status === 'pickup_scheduled' ? (
                          <><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /><span className="text-slate-700 dark:text-slate-300">Recogida programada ({approveResult.carrier_code?.toUpperCase()})</span></>
                        ) : (
                          <><AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" /><span className="text-slate-700 dark:text-slate-300">Sin recogida programada</span></>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {approveResult.has_label ? (
                          <><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /><span className="text-slate-700 dark:text-slate-300">Etiqueta generada</span></>
                        ) : (
                          <><AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" /><span className="text-slate-700 dark:text-slate-300">Sin etiqueta</span></>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {approveResult.email_sent ? (
                          <><CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /><span className="text-slate-700 dark:text-slate-300">Email enviado</span></>
                        ) : (
                          <><AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" /><span className="text-slate-700 dark:text-slate-300">Email no enviado</span></>
                        )}
                      </div>
                    </div>
                    {approveResult.pickup?.tracking_number && (
                      <p className="text-xs text-slate-500">Tracking: <span className="font-mono">{approveResult.pickup.tracking_number}</span></p>
                    )}
                    {approveResult.pickup_error && (
                      <p className="text-xs text-red-500">Error recogida: {approveResult.pickup_error}</p>
                    )}
                  </div>
                )}

                {/* Context-specific inputs */}
                {repair.status === 'requested' && (
                  <div className="space-y-3">
                    {repair.shipping_option === 'in_store' && (
                      <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-3">
                        <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">Entrega en tienda — no se programará recogida a domicilio</p>
                      </div>
                    )}
                    {repair.shipping_option !== 'in_store' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Transportista</label>
                        {(() => {
                          const cp = (repair.pickup_address?.postal_code || '').replace(/[-\s]/g, '')
                          const cc = repair.pickup_address?.country_code || 'ES'
                          const isBaleares = cc === 'ES' && cp.startsWith('07')
                          const carrierCode = isBaleares
                            ? (repairsConfig?.default_carrier_baleares || 'mrw')
                            : (repairsConfig?.default_carrier_peninsula || 'mrw')
                          const zona = isBaleares ? 'Baleares' : cc === 'PT' ? 'Portugal' : 'Península'
                          const label = `${carrierCode.toUpperCase()} (${zona})`
                          return <div className="px-3 py-2 text-sm bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium">{label}</div>
                        })()}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Fecha de recogida</label>
                        <input type="date" value={pickupDate} onChange={e => setPickupDate(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                      </div>
                    </div>
                    )}
                    {repair.shipping_option !== 'in_store' && (<>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Franja horaria de recogida</label>
                      <select value={pickupTimeSlot} onChange={e => setPickupTimeSlot(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white">
                        <option value="">Sin preferencia</option>
                        <option value="09:00-14:00">09:00 - 14:00 (Mañana)</option>
                        <option value="16:00-20:00">16:00 - 20:00 (Tarde)</option>
                        <option value="09:00-20:00">09:00 - 20:00 (Todo el día)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas para el transportista</label>
                      <textarea value={pickupNotes} onChange={e => setPickupNotes(e.target.value)} rows={2}
                        placeholder="Ej: Llamar al timbre 2B, dejar en portería, piso sin ascensor..."
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                    </div>
                    </>)}
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Motivo de rechazo (si rechaza)</label>
                      <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                    </div>
                  </div>
                )}

                {['received', 'diagnosing'].includes(repair.status) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Diagnóstico</label>
                    <textarea value={diagnosisNotes} onChange={e => setDiagnosisNotes(e.target.value)} rows={4} placeholder="Describe el diagnóstico del dispositivo..."
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                  </div>
                )}

                {repair.status === 'repairing' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas reparación</label>
                      <textarea value={repairNotes} onChange={e => setRepairNotes(e.target.value)} rows={3}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Horas reales trabajadas</label>
                      <input type="number" step="0.25" value={actualHours} onChange={e => setActualHours(e.target.value)} placeholder={String(repair.estimated_hours || 0)}
                        className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                    </div>
                  </div>
                )}

                {repair.status === 'quality_check' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas QC</label>
                    <textarea value={qcNotes} onChange={e => setQcNotes(e.target.value)} rows={2}
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                  </div>
                )}

                {/* Invoice + Payment flow - after QC passed or not repairable (PVP with diagnostic fee) */}
                {((repair.status === 'quality_check' && repair.qc_result === 'passed') || (repair.status === 'not_repairable' && (repair.diagnostic_fee || 0) > 0) || rejectedWithFee) && (
                  <div className="space-y-3">
                    {/* Step 1: Create invoice in Factusol */}
                    {!repair.invoice_number ? (
                      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                          <Receipt className="w-4 h-4" />Paso 1: Crear factura en Factusol
                        </h4>

                        {/* Cliente Factusol — vincular si la persona de contacto no coincide con la empresa */}
                        <div className="border-t border-blue-200 dark:border-blue-800/30 pt-3 -mx-4 px-4">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Cliente Factusol</label>
                            <button
                              type="button"
                              onClick={() => { setShowFactusolSearch(s => !s); setFactusolSearchResults([]); setFactusolSearchQuery('') }}
                              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 underline"
                            >
                              {repair.factusol_client ? 'Cambiar' : 'Vincular cliente existente'}
                            </button>
                          </div>
                          {repair.factusol_client ? (
                            <div className="bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-800/40 rounded p-2 text-xs">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="font-semibold text-emerald-700 dark:text-emerald-400">
                                    #{repair.factusol_client.code} · {repair.factusol_client.name}
                                  </div>
                                  <div className="text-slate-500 dark:text-slate-400">
                                    NIF: {repair.factusol_client.nif || '—'} · {repair.factusol_client.email || '—'}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!confirm('¿Quitar la vinculación? La factura usará los datos por defecto (NIF/email del contacto).')) return
                                    try {
                                      await api.post(`/repairs/admin/${repairId}/link-factusol-client`, { factusol_client_code: null })
                                      await loadRepair()
                                    } catch (err: any) { alert(err.response?.data?.detail || 'Error') }
                                  }}
                                  className="text-rose-500 hover:text-rose-600 text-xs"
                                >
                                  Quitar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              Sin vincular — se buscará por NIF/email del contacto. Si la factura debe ir a una empresa con datos distintos, vincúlala aquí.
                            </p>
                          )}

                          {showFactusolSearch && (
                            <div className="mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-2 space-y-2">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={factusolSearchQuery}
                                  onChange={e => setFactusolSearchQuery(e.target.value)}
                                  onKeyDown={async e => {
                                    if (e.key === 'Enter' && factusolSearchQuery.trim().length >= 2) {
                                      setFactusolSearching(true)
                                      try {
                                        const res = await api.get(`/repairs/admin/factusol-clients/search`, { params: { q: factusolSearchQuery.trim() } })
                                        setFactusolSearchResults(res.data.results || [])
                                      } catch (err: any) { alert(err.response?.data?.detail || 'Error buscando') }
                                      finally { setFactusolSearching(false) }
                                    }
                                  }}
                                  placeholder="Nombre, NIF o email (Enter para buscar)"
                                  className="flex-1 px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (factusolSearchQuery.trim().length < 2) return
                                    setFactusolSearching(true)
                                    try {
                                      const res = await api.get(`/repairs/admin/factusol-clients/search`, { params: { q: factusolSearchQuery.trim() } })
                                      setFactusolSearchResults(res.data.results || [])
                                    } catch (err: any) { alert(err.response?.data?.detail || 'Error buscando') }
                                    finally { setFactusolSearching(false) }
                                  }}
                                  disabled={factusolSearching || factusolSearchQuery.trim().length < 2}
                                  className="px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                                >
                                  {factusolSearching ? '...' : 'Buscar'}
                                </button>
                              </div>
                              {factusolSearchResults.length > 0 && (
                                <ul className="max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700 text-xs">
                                  {factusolSearchResults.map(c => (
                                    <li key={c.code} className="py-1.5 flex items-center justify-between gap-2">
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium text-slate-900 dark:text-white truncate">#{c.code} · {c.name}</div>
                                        <div className="text-slate-500 dark:text-slate-400 truncate">{c.nif || '—'} · {c.email || '—'}{c.city ? ` · ${c.city}` : ''}</div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          try {
                                            await api.post(`/repairs/admin/${repairId}/link-factusol-client`, { factusol_client_code: c.code })
                                            setShowFactusolSearch(false)
                                            setFactusolSearchResults([])
                                            setFactusolSearchQuery('')
                                            await loadRepair()
                                          } catch (err: any) { alert(err.response?.data?.detail || 'Error vinculando') }
                                        }}
                                        className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded"
                                      >
                                        Vincular
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {factusolSearchResults.length === 0 && !factusolSearching && factusolSearchQuery && (
                                <p className="text-xs text-slate-400 italic">Sin resultados — pulsa Enter o Buscar.</p>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Método de pago</label>
                            <select value={invoiceMethod} onChange={e => setInvoiceMethod(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white">
                              <option value="card">Tarjeta (003)</option>
                              <option value="transfer">Transferencia (001)</option>
                              <option value="direct_debit">Domiciliación (007)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Importe</label>
                            <div className="px-3 py-2 text-sm bg-slate-100 dark:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium">
                              {billsTransportFee ? `${repair.diagnostic_fee?.toFixed(2) || '29.90'} €` : `${repair.quote_total?.toFixed(2)} €`}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">{billsTransportFee ? 'Se creará factura de portes (recogida + devolución) en Factusol. El diagnóstico no se cobra.' : 'Se creará la factura con las líneas del presupuesto en Factusol (F_FAC + F_LFA).'}</p>
                        <button
                          onClick={async () => {
                            setInvoiceLoading(true)
                            try {
                              const res = await api.post(`/repairs/admin/${repairId}/create-invoice`, { payment_method: invoiceMethod })
                              alert(`Factura ${res.data.invoice_number} creada correctamente (${res.data.total?.toFixed(2)} €)`)
                              setPaymentMethod(invoiceMethod)
                              await loadRepair()
                              onUpdated()
                            } catch (err: any) { alert(err.response?.data?.detail || 'Error creando factura') }
                            finally { setInvoiceLoading(false) }
                          }}
                          disabled={invoiceLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          {invoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                          {invoiceLoading ? 'Creando factura...' : 'Crear factura en Factusol'}
                        </button>
                      </div>
                    ) : !repair.payment_invoice_pdf ? (
                      /* Step 2: Upload invoice PDF */
                      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
                          <Upload className="w-4 h-4" />Paso 2: Subir factura PDF
                        </h4>
                        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span>Factura <strong className="font-mono">{repair.invoice_number}</strong> creada — {repair.invoice_amount?.toFixed(2)} €</span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Factura PDF (generada desde Factusol)</label>
                          <input type="file" accept=".pdf" onChange={e => setPaymentInvoice(e.target.files?.[0] || null)}
                            className="w-full text-sm text-slate-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-slate-100 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-300" />
                        </div>
                        <button
                          onClick={async () => {
                            if (!paymentInvoice) { alert('Selecciona el archivo PDF de la factura'); return }
                            setInvoiceLoading(true)
                            try {
                              const formData = new FormData()
                              formData.append('invoice', paymentInvoice)
                              await api.post(`/repairs/admin/${repairId}/upload-invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                              setPaymentInvoice(null)
                              await loadRepair()
                              onUpdated()
                            } catch (err: any) { alert(err.response?.data?.detail || 'Error subiendo factura') }
                            finally { setInvoiceLoading(false) }
                          }}
                          disabled={invoiceLoading || !paymentInvoice}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          {invoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          {invoiceLoading ? 'Subiendo...' : 'Subir factura PDF'}
                        </button>
                      </div>
                    ) : (
                      /* Step 3: Request payment */
                      <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-4 space-y-3">
                        <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />Paso 3: Solicitar pago al cliente
                        </h4>
                        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" />Factura: <strong className="font-mono">{repair.invoice_number}</strong></span>
                          <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" />PDF subido</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Método de pago</label>
                            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white">
                              <option value="card">Tarjeta (Redsys)</option>
                              <option value="transfer">Transferencia bancaria</option>
                              <option value="direct_debit">Domiciliación</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Importe (EUR)</label>
                            <input type="number" step="0.01" value={paymentAmount || repair.invoice_amount || repair.quote_total || ''} onChange={e => setPaymentAmount(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                          </div>
                        </div>
                        {paymentMethod === 'card' && <p className="text-xs text-slate-500">Se enviará un enlace de pago Redsys al email del cliente.</p>}
                        {paymentMethod === 'transfer' && <p className="text-xs text-slate-500">Se enviará email con IBAN y factura adjunta.</p>}
                        {paymentMethod === 'direct_debit' && <p className="text-xs text-slate-500">Se enviará email informativo con la factura.</p>}
                      </div>
                    )}
                  </div>
                )}

                {/* Payment pending info */}
                {repair.status === 'payment_pending' && (
                  <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">Pago pendiente</h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div><span className="text-slate-500">Método:</span> <strong>{repair.payment_request_method === 'card' ? 'Tarjeta' : repair.payment_request_method === 'transfer' ? 'Transferencia' : 'Domiciliación'}</strong></div>
                      <div><span className="text-slate-500">Importe:</span> <strong>{repair.payment_request_amount?.toFixed(2)} €</strong></div>
                      <div><span className="text-slate-500">Solicitado:</span> {repair.payment_request_at ? new Date(repair.payment_request_at).toLocaleString('es-ES') : ''}</div>
                    </div>
                    {repair.payment_invoice_pdf && <p className="text-xs text-slate-500 mt-2">Factura: {repair.payment_invoice_pdf}</p>}
                  </div>
                )}

                {/* Payment confirmed info */}
                {repair.status === 'payment_confirmed' && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Pago confirmado</h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div><span className="text-slate-500">Método:</span> <strong>{repair.payment_request_method === 'card' ? 'Tarjeta' : repair.payment_request_method === 'transfer' ? 'Transferencia' : 'Domiciliación'}</strong></div>
                      <div><span className="text-slate-500">Importe:</span> <strong>{repair.payment_request_amount?.toFixed(2)} €</strong></div>
                      <div><span className="text-slate-500">Confirmado:</span> {repair.payment_confirmed_at ? new Date(repair.payment_confirmed_at).toLocaleString('es-ES') : ''} {repair.payment_confirmed_by ? `(${repair.payment_confirmed_by})` : ''}</div>
                    </div>
                  </div>
                )}

                {repair.status === 'quote_approved' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas (esperando piezas)</label>
                    <textarea value={waitingPartsNotes} onChange={e => setWaitingPartsNotes(e.target.value)} rows={2} placeholder="Qué piezas se han pedido..."
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                  </div>
                )}

                {repair.status === 'delivered' && (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Método pago</label>
                      <select value={closePayment.method} onChange={e => setClosePayment({...closePayment, method: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white">
                        <option value="transferencia">Transferencia</option>
                        <option value="tarjeta">Tarjeta</option>
                        <option value="bizum">Bizum</option>
                        <option value="paypal">PayPal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nº Factura</label>
                      <input value={closePayment.invoice_number} onChange={e => setClosePayment({...closePayment, invoice_number: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Importe</label>
                      <input type="number" step="0.01" value={closePayment.invoice_amount} onChange={e => setClosePayment({...closePayment, invoice_amount: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {actions.map((a, i) => {
                    const Icon = a.icon
                    return (
                      <button key={i} onClick={a.action} disabled={actionLoading}
                        className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${a.color}`}>
                        <Icon className="w-4 h-4" />{a.label}
                      </button>
                    )
                  })}
                  {canCancel && (
                    <button
                      onClick={() => { if (cancelReason || confirm('¿Cancelar sin motivo?')) doAction('cancel', { reason: cancelReason || 'Cancelada' }) }}
                      disabled={actionLoading}
                      className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      <XCircle className="w-4 h-4" />Cancelar
                    </button>
                  )}
                </div>

                {canCancel && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Motivo cancelación (opcional)</label>
                    <input value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                  </div>
                )}

                {actions.length === 0 && !canCancel && (
                  <p className="text-sm text-slate-500 italic">No hay acciones disponibles en este estado</p>
                )}
              </div>
            )}

            {/* Quote tab */}
            {activeSection === 'quote' && (
              <div className="space-y-4">
                {repair.quote_total > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase mb-3">Resumen costes</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-slate-500">Mano de obra</span><span className="font-medium">{repair.quote_labor_cost?.toFixed(2)} €</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Piezas</span><span className="font-medium">{repair.quote_parts_cost?.toFixed(2)} €</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Transporte</span><span className="font-medium">{repair.quote_shipping_cost?.toFixed(2)} €</span></div>
                          <div className="border-t border-slate-200 dark:border-slate-600 pt-2 flex justify-between font-medium"><span>Coste total</span><span>{repair.quote_subtotal?.toFixed(2)} €</span></div>
                        </div>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase mb-3">Precio cliente</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-slate-500">Margen ({repair.quote_margin_percent}%)</span><span className="font-medium">+{(repair.quote_total_net - repair.quote_subtotal)?.toFixed(2)} €</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">Subtotal neto</span><span className="font-medium">{repair.quote_total_net?.toFixed(2)} €</span></div>
                          <div className="flex justify-between"><span className="text-slate-500">IVA</span><span className="font-medium">+{repair.quote_vat_amount?.toFixed(2)} €</span></div>
                          <div className="border-t border-slate-200 dark:border-slate-600 pt-2 flex justify-between font-bold text-base"><span>Total cliente</span><span className="text-indigo-600 dark:text-indigo-400">{repair.quote_total?.toFixed(2)} €</span></div>
                        </div>
                      </div>
                    </div>

                    {/* Quote items */}
                    {repair.quote_items?.length > 0 && (
                      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr className="text-left text-slate-500">
                              <th className="px-4 py-2 font-medium">Concepto</th>
                              <th className="px-4 py-2 font-medium">Tipo</th>
                              <th className="px-4 py-2 font-medium text-right">Coste</th>
                              <th className="px-4 py-2 font-medium text-right">Venta</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {repair.quote_items.map((qi: any, i: number) => (
                              <tr key={i}>
                                <td className="px-4 py-2 text-slate-900 dark:text-white">
                                  {qi.description}
                                  {qi.factusol_reference && <span className="ml-2 text-xs text-slate-400">({qi.factusol_reference})</span>}
                                  {qi.estimated_hours > 0 && <span className="ml-2 text-xs text-slate-400">{qi.estimated_hours}h</span>}
                                </td>
                                <td className="px-4 py-2">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${qi.item_type === 'catalog_part' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : qi.item_type === 'shipping' ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' : qi.item_type === 'custom_part' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                    {qi.item_type === 'catalog_part' ? 'Factusol' : qi.item_type === 'shipping' ? 'Transporte' : qi.item_type === 'custom_part' ? 'Manual' : qi.item_type}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-right text-slate-500">{qi.total_cost?.toFixed(2)} €</td>
                                <td className="px-4 py-2 text-right font-medium text-slate-900 dark:text-white">{qi.total_sale?.toFixed(2)} €</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Quote status info */}
                    <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                      {repair.quote_sent_at && <span>Enviado: {new Date(repair.quote_sent_at).toLocaleString('es-ES')}</span>}
                      {repair.quote_valid_until && <span>Válido hasta: {new Date(repair.quote_valid_until).toLocaleDateString('es-ES')}</span>}
                      {repair.quote_response && <span>Respuesta: <strong className={repair.quote_response === 'approved' ? 'text-emerald-600' : 'text-red-600'}>{repair.quote_response === 'approved' ? 'Aceptado' : 'Rechazado'}</strong></span>}
                      {repair.quote_response_nif && <span>NIF: <strong className="font-mono">{repair.quote_response_nif}</strong></span>}
                    </div>
                    {repair.quote_response_signature && (
                      <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-700/30 p-3 rounded-lg">
                        <span className="text-xs text-slate-500">Firma cliente:</span>
                        <img src={repair.quote_response_signature} alt="Firma cliente" className="h-12 border border-slate-200 dark:border-slate-600 rounded bg-white" />
                      </div>
                    )}
                    {repair.quote_notes && <p className="text-sm text-slate-500 bg-slate-50 dark:bg-slate-700/30 p-3 rounded-lg">{repair.quote_notes}</p>}

                    {/* Reenviar presupuesto a otro email */}
                    <div className="bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-slate-400 uppercase mb-3">Enviar copia del presupuesto</h4>
                      {repair.customer_email && (
                        <button
                          onClick={async () => {
                            if (!confirm(`¿Reenviar el presupuesto a ${repair.customer_email}?`)) return
                            setResendingToClient(true)
                            try {
                              await api.post(`/repairs/admin/${repair.id}/resend-quote`, { email: repair.customer_email })
                              alert(`Presupuesto reenviado a ${repair.customer_email}`)
                            } catch (err: any) {
                              alert(err?.response?.data?.detail || 'Error enviando presupuesto')
                            } finally {
                              setResendingToClient(false)
                            }
                          }}
                          disabled={resendingToClient}
                          className="w-full mb-3 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {resendingToClient ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Reenviar al cliente ({repair.customer_email})
                        </button>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="email"
                          placeholder="email@ejemplo.com"
                          value={resendQuoteEmail}
                          onChange={e => setResendQuoteEmail(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                        />
                        <button
                          onClick={async () => {
                            if (!resendQuoteEmail || !resendQuoteEmail.includes('@')) return
                            setResendingQuote(true)
                            try {
                              await api.post(`/repairs/admin/${repair.id}/resend-quote`, { email: resendQuoteEmail })
                              alert(`Presupuesto enviado a ${resendQuoteEmail}`)
                              setResendQuoteEmail('')
                            } catch (err: any) {
                              alert(err?.response?.data?.detail || 'Error enviando presupuesto')
                            } finally {
                              setResendingQuote(false)
                            }
                          }}
                          disabled={resendingQuote || !resendQuoteEmail}
                          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {resendingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Enviar
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 italic">No hay presupuesto creado aún</p>
                )}
              </div>
            )}

            {/* Timeline tab */}
            {activeSection === 'timeline' && (
              <div className="space-y-3">
                {repair.history?.length > 0 ? (
                  [...repair.history].reverse().map((h: any, i: number) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-2 h-2 mt-2 rounded-full bg-indigo-400 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-900 dark:text-white">{h.description}</p>
                        <p className="text-xs text-slate-500">{h.created_at ? new Date(h.created_at).toLocaleString('es-ES') : ''} {h.created_by ? `• ${h.created_by}` : ''}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500 italic">Sin historial</p>
                )}
              </div>
            )}

            {/* Comments tab */}
            {activeSection === 'comments' && (() => {
              const token = localStorage.getItem('jvs_auth_token') || ''
              return (
              <div className="space-y-4">
                {repair.comments?.map((c: any) => (
                  <div key={c.id} className={`p-3 rounded-lg ${c.is_public ? 'bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800' : 'bg-slate-50 dark:bg-slate-700/30'}`}>
                    {editingCommentId === c.id ? (
                      <div className="space-y-2">
                        <textarea value={editCommentText} onChange={e => setEditCommentText(e.target.value)} rows={2}
                          className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                            <input type="checkbox" checked={editCommentPublic} onChange={e => setEditCommentPublic(e.target.checked)} className="rounded" />
                            Visible para el cliente
                          </label>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => updateComment(c.id)} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Guardar</button>
                            <button onClick={() => setEditingCommentId(null)} className="px-3 py-1 text-xs bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300">Cancelar</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900 dark:text-white">{c.user_name}</span>
                            {c.is_public && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Público</span>}
                            <span className="text-xs text-slate-400">{c.created_at ? new Date(c.created_at).toLocaleString('es-ES') : ''}</span>
                            {c.updated_at && <span className="text-[10px] text-slate-400 italic">(editado)</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="cursor-pointer text-slate-400 hover:text-indigo-500 transition-colors" title="Adjuntar imagen">
                              <Paperclip className="w-3.5 h-3.5" />
                              <input type="file" accept="image/*" multiple className="hidden"
                                onChange={e => { if (e.target.files?.length) uploadAttachmentsToExisting(c.id, e.target.files); e.target.value = '' }} />
                            </label>
                            <button onClick={() => openCamera(c.id)} className="text-slate-400 hover:text-indigo-500 transition-colors" title="Foto con cámara">
                              <Camera className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => { setEditingCommentId(c.id); setEditCommentText(c.comment); setEditCommentPublic(c.is_public) }}
                              className="text-slate-400 hover:text-indigo-500 transition-colors" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteComment(c.id)}
                              className="text-slate-400 hover:text-red-500 transition-colors" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{c.comment}</p>
                        {/* Attachments */}
                        {c.attachments?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {c.attachments.map((att: any) => (
                              <a key={att.id} href={`/api/repairs/comment-attachment/${att.id}?token=${token}`} target="_blank" rel="noreferrer"
                                className="block rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600 hover:border-blue-400 transition-colors">
                                <img src={`/api/repairs/comment-attachment/${att.id}?token=${token}`} alt={att.filename}
                                  className="h-20 w-auto max-w-[120px] object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                  <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Escribe un comentario..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-2" />
                  {/* File preview */}
                  {commentFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {commentFiles.map((f, i) => (
                        <div key={i} className="relative group">
                          <img src={URL.createObjectURL(f)} alt={f.name} className="h-16 w-auto rounded-lg border border-slate-200 object-cover" />
                          <button onClick={() => setCommentFiles(prev => prev.filter((_, idx) => idx !== i))}
                            className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400" title="Si se marca, se enviará un email al cliente con el comentario y sus imágenes">
                        <input type="checkbox" checked={commentPublic} onChange={e => setCommentPublic(e.target.checked)} className="rounded" />
                        Visible para el cliente {commentPublic && <span className="text-[11px] text-indigo-500">(se enviará email)</span>}
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-500 cursor-pointer transition-colors">
                        <Paperclip className="w-4 h-4" />
                        <span>Adjuntar</span>
                        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                          onChange={e => { if (e.target.files) setCommentFiles(prev => [...prev, ...Array.from(e.target.files!)]) }} />
                      </label>
                      <button type="button" onClick={() => openCamera('new')}
                        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-500 transition-colors">
                        <Camera className="w-4 h-4" />
                        <span>Cámara</span>
                      </button>
                    </div>
                    <button onClick={addComment} disabled={!comment.trim() && commentFiles.length === 0}
                      className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                      Añadir
                    </button>
                  </div>
                  {commentEmailStatus && (
                    <p className={`mt-2 text-xs ${commentEmailStatus.ok ? 'text-green-600' : 'text-red-600'}`}>
                      {commentEmailStatus.ok ? '✓ ' : '⚠ '}{commentEmailStatus.msg}
                    </p>
                  )}
                </div>
              </div>
            )})()}
          </div>
        </div>
      </div>

      {/* Camera modal */}
      {showCamera && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={() => { stopCamera(); setShowCamera(false) }}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Camera className="w-4 h-4 text-indigo-500" />Captura de cámara
              </h3>
              <button onClick={() => { stopCamera(); setShowCamera(false) }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-black flex items-center justify-center" style={{ minHeight: 360 }}>
              <video ref={videoRef} autoPlay playsInline muted className="max-w-full max-h-[50vh]" />
            </div>
            <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700">
              <button onClick={capturePhoto}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors">
                <Camera className="w-5 h-5" />Capturar foto
              </button>
              <button onClick={() => { stopCamera(); setShowCamera(false) }}
                className="px-4 py-2.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote modal */}
      {showQuoteModal && (
        <QuoteModal
          repairId={repairId}
          repair={repair}
          onClose={() => setShowQuoteModal(false)}
          onSent={() => { setShowQuoteModal(false); loadRepair(); onUpdated() }}
        />
      )}

      {/* No reparable modal */}
      {showNotRepairableModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowNotRepairableModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">Marcar como no reparable</h3>
              </div>
              <button onClick={() => setShowNotRepairableModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Motivo</label>
                <textarea
                  value={notRepairableReason}
                  onChange={e => setNotRepairableReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Describe por qué el equipo no es reparable..."
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notRepairableGenerateLabel}
                  onChange={e => setNotRepairableGenerateLabel(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">Generar etiqueta de devolución</span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Desmarca si el cliente abandona el equipo, lo recoge en persona o se va a achatarrar.</p>
                </div>
              </label>
              {!notRepairableGenerateLabel && (
                <div className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 rounded-lg px-3 py-2">
                  No se generará envío de devolución. Tras esto podrás cerrar el caso directamente.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 rounded-b-xl">
              <button onClick={() => setShowNotRepairableModal(false)} className="px-3 py-1.5 text-sm rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700">Cancelar</button>
              <button
                disabled={!notRepairableReason.trim() || actionLoading}
                onClick={() => {
                  const reason = notRepairableReason.trim()
                  if (!reason) return
                  setShowNotRepairableModal(false)
                  doAction('not-repairable', { reason, generate_return_label: notRepairableGenerateLabel })
                }}
                className="px-4 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
